import type { ISerialize } from '@/objects/ISerialize';
import { Category } from './Category';
import { CategoryGroupMap } from './CategoryGroupMap';
import { RaceContestantDetails } from './RaceContestantDetails';
import { ImportDuplicateContestant } from './ImportDuplicateContestant';
import { EventinfoBase } from './EventinfoBase';
import { EventSetup } from './EventSetup';
import { ImportFormat } from './ImportFormat';
import { Race } from './Race';
import type { ExternalDatasourceType } from './../enums/ExternalDatasourceType';

export class ExternalEventDatasourceResponse implements ISerialize {
    public Categories: Category[];

    public CategoryGroupConnection: CategoryGroupMap[];

    public Contestants: RaceContestantDetails[];

    public Duplicates: ImportDuplicateContestant[];

    public EventInfo: EventinfoBase | null;

    public EventSetup: EventSetup | null;

    public ExternalDatasourceType: ExternalDatasourceType;

    public GroupToRaceOneToOne: boolean;

    public HasRelayTeams: boolean;

    public ImportFormat: ImportFormat | null;

    public Races: Race[];

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Categories?: Category[];
        CategoryGroupConnection?: CategoryGroupMap[];
        Contestants?: RaceContestantDetails[];
        Duplicates?: ImportDuplicateContestant[];
        EventInfo?: EventinfoBase | null;
        EventSetup?: EventSetup | null;
        ExternalDatasourceType?: ExternalDatasourceType;
        GroupToRaceOneToOne?: boolean;
        HasRelayTeams?: boolean;
        ImportFormat?: ImportFormat | null;
        Races?: Race[];
    }) {
        this.Categories = values.Categories ?? [];

        this.CategoryGroupConnection = values.CategoryGroupConnection ?? [];

        this.Contestants = values.Contestants ?? [];

        this.Duplicates = values.Duplicates ?? [];

        this.EventInfo = values.EventInfo ?? null;

        this.EventSetup = values.EventSetup ?? null;

        this.ExternalDatasourceType = values.ExternalDatasourceType ?? 'Oauth';

        this.GroupToRaceOneToOne = values.GroupToRaceOneToOne ?? false;

        this.HasRelayTeams = values.HasRelayTeams ?? false;

        this.ImportFormat = values.ImportFormat ?? null;

        this.Races = values.Races ?? [];
    }

    public serialize(): string {
        return (
            '{' +
            `"Categories": [ ${this.Categories.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"CategoryGroupConnection": [ ${this.CategoryGroupConnection.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"Contestants": [ ${this.Contestants.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"Duplicates": [ ${this.Duplicates.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"EventInfo": ${!this.EventInfo ? null : this.EventInfo?.serialize()}` +
            ',' +
            `"EventSetup": ${!this.EventSetup ? null : this.EventSetup?.serialize()}` +
            ',' +
            `"ExternalDatasourceType": ${this.ExternalDatasourceType == 'Oauth' ? 0 : this.ExternalDatasourceType == 'Bearer' ? 1 : this.ExternalDatasourceType == 'FileDrop' ? 2 : 0}` +
            ',' +
            `"GroupToRaceOneToOne": ${JSON.stringify(this.GroupToRaceOneToOne)}` +
            ',' +
            `"HasRelayTeams": ${JSON.stringify(this.HasRelayTeams)}` +
            ',' +
            `"ImportFormat": ${!this.ImportFormat ? null : this.ImportFormat?.serialize()}` +
            ',' +
            `"Races": [ ${this.Races.map((x) => (!x ? null : x?.serialize()))} ]` +
            '}'
        );
    }

    /**
     * Creates an instance of ExternalEventDatasourceResponse from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Categories?: Category[];
        CategoryGroupConnection?: CategoryGroupMap[];
        Contestants?: RaceContestantDetails[];
        Duplicates?: ImportDuplicateContestant[];
        EventInfo?: EventinfoBase | null;
        EventSetup?: EventSetup | null;
        ExternalDatasourceType?: ExternalDatasourceType;
        GroupToRaceOneToOne?: boolean;
        HasRelayTeams?: boolean;
        ImportFormat?: ImportFormat | null;
        Races?: Race[];
    }): ExternalEventDatasourceResponse {
        return new ExternalEventDatasourceResponse(values);
    }

    public static _FromServerJson(json: any): ExternalEventDatasourceResponse {
        return new ExternalEventDatasourceResponse({
            Categories: json.Categories?.map((x: any) => Category._FromServerJson(x)),
            CategoryGroupConnection: json.CategoryGroupConnection?.map((x: any) => CategoryGroupMap._FromServerJson(x)),
            Contestants: json.Contestants?.map((x: any) => RaceContestantDetails._FromServerJson(x)),
            Duplicates: json.Duplicates?.map((x: any) => ImportDuplicateContestant._FromServerJson(x)),
            EventInfo: json.EventInfo ? EventinfoBase._FromServerJson(json.EventInfo) : null,
            EventSetup: json.EventSetup ? EventSetup._FromServerJson(json.EventSetup) : null,
            ExternalDatasourceType:
                json.ExternalDatasourceType == 0
                    ? 'Oauth'
                    : json.ExternalDatasourceType == 1
                      ? 'Bearer'
                      : json.ExternalDatasourceType == 2
                        ? 'FileDrop'
                        : 'Oauth',
            GroupToRaceOneToOne: json.GroupToRaceOneToOne,
            HasRelayTeams: json.HasRelayTeams,
            ImportFormat: json.ImportFormat ? ImportFormat._FromServerJson(json.ImportFormat) : null,
            Races: json.Races?.map((x: any) => Race._FromServerJson(x)),
        });
    }

    /**
     * Creates an instance of ExternalEventDatasourceResponse from another instance of ExternalEventDatasourceResponse.
     * @param other The ExternalEventDatasourceResponse you want to clone
     * @returns A cloned ExternalEventDatasourceResponse
     */
    public static FromOther(other: ExternalEventDatasourceResponse): ExternalEventDatasourceResponse {
        return ExternalEventDatasourceResponse._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ExternalEventDatasourceResponse.FromOther(this);
    }

    static NAME_OF_CLASS = 'ExternalEventDatasourceResponse';
}
